/**
 * @file lighting.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-21
 * @description HID EEPROM 键盘灯光控制共享工具函数
 *
 * 纯函数集合，不持有任何状态，不定义任何数据。
 * 每个函数接收所需的全部参数（地址、命令、回调等），
 * 各芯片自行调用并传入芯片特有数据。
 *
 * 使用方式：
 * ```ts
 * class MyLighting extends KeyboardLightingFeature {
 *   async setCurrentEffect(effect: number) {
 *     return shared.setCurrentEffect(this.device, sendWrite, 220, MY_ADDRESS.BACKLIGHT, effect)
 *   }
 * }
 * ```
 */

import {
  keyboardLightingCustomConf,
  keyMapLighting,
  keyRgbItem,
  type AuxLightingEnableRegister,
} from '../../../../types/keyboardLighting'
import type { LightingEffectDefinition, LightingCapability } from '../../../../types/lighting'
import {
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_EFFECT_KIND,
  LIGHTING_TARGET,
  type KeyboardLightingTarget,
} from '../../../../types/lighting'
import { LightingConfigData, LightingDomainType } from '../../../../types/lightingDomain'
import { LightingDomainRegistry } from '../../../../features/keyboard-lighting/LightingDomainRegistry'
import type { RGB, RGBGroup } from '../../../../types/color'
import type { CommonAddress, LevelAddress } from '../../../../types/protocol'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { delay } from '../../../../utils/TimeUtils'
import { convertStrToASCII } from '../../../../utils/StringUtils'
import {
  getAddress,
  createSendRead,
  createSendWrite,
  createCheckACK,
} from '../../../../utils/common'
import type { LayoutKey } from '../../../../types/manifest'
import type { BaseDevice } from '../../../../runtime/BaseDevice'

// ==================== key 索引缓存 ====================

const keyIndexSetCache = new WeakMap<LayoutKey[][], Set<number>>()

function getKeyIndexSet(layoutKeys: LayoutKey[][]): Set<number> {
  const cached = keyIndexSetCache.get(layoutKeys)
  if (cached) return cached

  const set = new Set<number>()
  for (const row of layoutKeys) {
    for (const key of row) {
      if (key !== undefined && key !== null) {
        set.add(key.key)
      }
    }
  }

  keyIndexSetCache.set(layoutKeys, set)
  return set
}

type SendRead = ReturnType<typeof createSendRead>
type SendWrite = ReturnType<typeof createSendWrite>
type CheckACK = ReturnType<typeof createCheckACK>

// ==================== 灯效档位校验 ====================

/** 亮度/速度档位下限 */
export const LIGHTING_LEVEL_MIN = 0
/** 亮度/速度档位上限 */
export const LIGHTING_LEVEL_MAX = 100
/** 档位无效时的回落值 */
export const LIGHTING_LEVEL_DEFAULT = 60

/**
 * 校验亮度/速度档位。
 *
 * 越界一律回落到 `fallback` 而不是裁剪：未初始化的 Flash 扇区读回 0xFF，
 * 裁剪成 100 恰好就是「亮度拉满」的故障表象，必须与真实的 100 区分开。
 *
 * 读写两侧都要过一遍：`parse` 侧拦住坏读进入业务层，`serialize` 侧拦住坏值
 * 被「读-改-写」固化进 Flash。
 */
export function sanitizeLightingLevel(
  value: unknown,
  fallback: number = LIGHTING_LEVEL_DEFAULT,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  const level = Math.round(value)
  if (level < LIGHTING_LEVEL_MIN || level > LIGHTING_LEVEL_MAX) return fallback
  return level
}

// ==================== 行列工具 ====================

/** 行优先：index = row * columnCount + col */
export function rowColToIndex(row: number, col: number, columnCount: number): number {
  return row * columnCount + col
}

/** 将行优先 index 转为行列 */
export function indexToRowCol(index: number, columnCount: number): { row: number; col: number } {
  return {
    row: Math.floor(index / columnCount),
    col: index % columnCount,
  }
}

/** 校验按键行列是否在键盘矩阵范围内 */
export function isValidRowCol(
  row: number,
  col: number,
  columnCount: number,
  rowCount: number,
  layoutKeys: LayoutKey[][],
): boolean {
  if (row >= rowCount || col >= columnCount) return false

  // key 值本身即行优先索引，用 rowColToIndex 算出索引后查 key-index-set
  const index = rowColToIndex(row, col, columnCount)
  const keySet = getKeyIndexSet(layoutKeys)
  return keySet.has(index)
}

// ==================== 元数据 ====================

/** 获取已注册的所有灯效领域类型 */
export function getSupportedDomains(): LightingDomainType[] {
  return LightingDomainRegistry.getAllTypes()
}

/**
 * 读取 manifest 中配置的灯效列表，合并自定义灯效。
 * @param device - 设备实例
 * @param sendRead - 芯片的 sendRead 函数
 * @param target - 灯效目标（键盘/侧灯）
 */
export async function getEffects(
  device: BaseDevice,
  sendRead: SendRead,
  target: KeyboardLightingTarget = LIGHTING_TARGET.Keyboard,
): Promise<LightingEffectDefinition[]> {
  const lightingConfig = device.manifest.capabilities.find(
    (cap): cap is LightingCapability => cap.type === 'lighting',
  )
  if (!lightingConfig) return []

  // 配置中target对应的预设灯效
  const effects = lightingConfig.config.effects.filter((effect) => effect.target === target)

  // 用户自定义灯效，目前只有键盘背光灯效能自定义
  const customEffects: LightingEffectDefinition[] = []
  if (
    target === LIGHTING_TARGET.Keyboard &&
    LightingDomainRegistry.has(LightingDomainType.Static)
  ) {
    const domain = LightingDomainRegistry.get(LightingDomainType.Static)
    if (domain.hasCustomEffects && domain.customEffectResolver) {
      const { nameAddress, readSize, parseName, parseType } = domain.customEffectResolver
      // 计算预设静态灯效数量，自定义灯效从预设之后开始
      const presetStaticCount = lightingConfig.config.effects.filter(
        (e) =>
          e.target === LIGHTING_TARGET.Keyboard &&
          e.kind === LIGHTING_EFFECT_KIND.Static &&
          !('isCustom' in e),
      ).length
      const startIdx = Math.max(presetStaticCount, 0)
      for (let i = startIdx; i < nameAddress.length; i++) {
        const addr = nameAddress[i]
        try {
          const result = await sendRead(
            device,
            addr,
            readSize,
            (data: DataView) => {
              const name = parseName(data)
              const staticType = parseType(data)
              return {
                id: i,
                i18nKey: name,
                target: LIGHTING_TARGET.Keyboard,
                kind: LIGHTING_EFFECT_KIND.Static,
                editableFields: [2, 3].includes(staticType)
                  ? [
                      LIGHTING_EDITABLE_FIELD.Brightness,
                      LIGHTING_EDITABLE_FIELD.Color,
                      LIGHTING_EDITABLE_FIELD.Gradient,
                    ]
                  : [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
                staticType,
                isCustom: true,
              } as LightingEffectDefinition
            },
            [`light:static:${i}`],
          )
          if (result.staticType && [1, 2, 3].includes(result.staticType)) {
            customEffects.push(result)
          }
        } catch {
          // 自定义灯效读取失败（无硬件连接等情况），跳过该项
        }
      }
    }
  }

  const effectMap = new Map<number, LightingEffectDefinition>()
  ;[...effects, ...customEffects].forEach((item) => effectMap.set(item.id, item))
  return Array.from(effectMap.values())
}

// ==================== 领域通用 CRUD ====================

/** 读取指定领域的完整配置，由 LightingDomainDescriptor 驱动 */
export async function getConfig(
  device: BaseDevice,
  sendRead: SendRead,
  domain: LightingDomainType,
  index: number,
): Promise<LightingConfigData> {
  const descriptor = LightingDomainRegistry.get(domain)
  const realIndex = descriptor.normalizeIndex?.(index) ?? index
  const addr = descriptor.addressResolver.getBaseAddr(device.currentConfig.index, realIndex)
  const tags = descriptor?.cacheTags?.(index, device.currentConfig.index) ?? []
  return sendRead(
    device,
    addr,
    descriptor.configSize,
    (data: DataView) => {
      const buffer = new Uint8Array(descriptor.configSize)
      console.log(data)
      for (let i = 0; i < descriptor.configSize; i++) {
        buffer[i] = data.getUint8(4 + i)
      }
      return descriptor.parser.parse(buffer, index)
    },
    tags,
  )
}

/** 写入指定领域的完整配置 */
export async function setConfig(
  device: BaseDevice,
  sendRead: SendRead,
  sendWrite: SendWrite,
  domain: LightingDomainType,
  index: number,
  config: Partial<LightingConfigData>,
): Promise<void> {
  const descriptor = LightingDomainRegistry.get(domain)
  const current = await getConfig(device, sendRead, domain, index)
  const merged = { ...current, ...config }
  const buffer = descriptor.parser.serialize(merged as LightingConfigData)
  const realIndex = descriptor.normalizeIndex?.(index) ?? index
  const addr = descriptor.addressResolver.getBaseAddr(device.currentConfig.index, realIndex)
  return sendWrite(device, addr, Array.from(buffer))
}

/** 设置指定领域的单个属性（亮度/速度/颜色等） */
export async function setProperty(
  device: BaseDevice,
  sendRead: SendRead,
  sendWrite: SendWrite,
  domain: LightingDomainType,
  index: number,
  key: string,
  value: number | boolean | RGB | RGBGroup,
): Promise<void> {
  const descriptor = LightingDomainRegistry.get(domain)
  const current = await getConfig(device, sendRead, domain, index)
  const updated: LightingConfigData = { ...current, [key]: value }
  const buffer = descriptor.parser.serialize(updated)
  const realIndex = descriptor.normalizeIndex?.(index) ?? index
  const addr = descriptor.addressResolver.getBaseAddr(device.currentConfig.index, realIndex)
  return sendWrite(device, addr, Array.from(buffer))
}

/** 读取指定领域的单个属性 */
export async function getProperty(
  device: BaseDevice,
  sendRead: SendRead,
  domain: LightingDomainType,
  index: number,
  key: string,
): Promise<number | boolean | RGB | RGBGroup> {
  const config = await getConfig(device, sendRead, domain, index)
  return { ...config }[key] as number | boolean | RGB | RGBGroup
}

// ==================== 背光开关 ====================

/** 设置键盘背光开关（0=关, 1=开, 0xff=不支持背光） */
export async function setBacklightSwitch(
  device: BaseDevice,
  sendWrite: SendWrite,
  flashSettleMs: number,
  backlightSwitch: LevelAddress,
  enabled: boolean,
): Promise<void> {
  const addr = getAddress(backlightSwitch, device.currentConfig.index)
  await sendWrite(device, addr, [enabled ? 1 : 0])
  await delay(flashSettleMs)
}

/** 读取键盘背光开关状态 */
export async function getBacklightSwitch(
  device: BaseDevice,
  sendRead: SendRead,
  backlightSwitch: LevelAddress,
): Promise<number> {
  const addr = getAddress(backlightSwitch, device.currentConfig.index)
  return sendRead(device, addr, 8, (data: DataView) => data.getUint8(4), [
    `cfg:${device.currentConfig.index}`,
  ])
}

// ==================== 设备级操作 ====================

/** 设置当前灯效 ID */
export async function setCurrentEffect(
  device: BaseDevice,
  sendWrite: SendWrite,
  flashSettleMs: number,
  backlightEffect: LevelAddress,
  effect: number,
): Promise<void> {
  const addr = getAddress(backlightEffect, device.currentConfig.index)
  await sendWrite(device, addr, [effect])
  await delay(flashSettleMs)
}

/** 读取当前生效的灯效 ID */
export async function getCurrentEffect(
  device: BaseDevice,
  sendRead: SendRead,
  backlightEffect: LevelAddress,
): Promise<number> {
  const addr = getAddress(backlightEffect, device.currentConfig.index)
  // 固件要求最小读长度 8 字节，读 1 字节会导致 NotAllowedError
  return sendRead(device, addr, 8, (data: DataView) => data.getUint8(4), [
    `cfg:${device.currentConfig.index}`,
  ])
}

/** 设置键盘背光全局亮度（1-100） */
export async function setBrightness(
  device: BaseDevice,
  sendWrite: SendWrite,
  flashSettleMs: number,
  globalBrightness: LevelAddress,
  brightness: number,
): Promise<void> {
  const brightnessValue = Math.min(100, Math.max(1, brightness))
  const addr = getAddress(globalBrightness, device.currentConfig.index)
  await sendWrite(device, addr, [brightnessValue])
  await delay(flashSettleMs)
}

/** 读取键盘背光全局亮度 */
export async function getBrightness(
  device: BaseDevice,
  sendRead: SendRead,
  globalBrightness: LevelAddress,
): Promise<number> {
  const addr = getAddress(globalBrightness, device.currentConfig.index)
  // 固件要求最小读长度 8 字节，读 1 字节会导致 NotAllowedError
  return sendRead(device, addr, 8, (data: DataView) => data.getUint8(4), [
    `cfg:${device.currentConfig.index}`,
  ])
}

/** 设置键盘背光全局速度（1-100） */
export async function setSpeed(
  device: BaseDevice,
  sendWrite: SendWrite,
  flashSettleMs: number,
  globalSpeed: LevelAddress,
  speed: number,
): Promise<void> {
  const speedValue = Math.min(100, Math.max(1, speed))
  const addr = getAddress(globalSpeed, device.currentConfig.index)
  await sendWrite(device, addr, [speedValue])
  await delay(flashSettleMs)
}

/** 读取键盘背光全局速度 */
export async function getSpeed(
  device: BaseDevice,
  sendRead: SendRead,
  globalSpeed: LevelAddress,
): Promise<number> {
  const addr = getAddress(globalSpeed, device.currentConfig.index)
  // 固件要求最小读长度 8 字节，读 1 字节会导致 NotAllowedError
  return sendRead(device, addr, 8, (data: DataView) => data.getUint8(4), [
    `cfg:${device.currentConfig.index}`,
  ])
}

// ==================== 逐键 RGB ====================

/**
 * 逐键设置静态灯效 RGB 颜色。
 * 通过锁定缓存 -> 逐键写入 -> 解锁缓存 -> 失效 tag 的流程确保数据一致性。
 */
export async function setKeysColor(
  device: BaseDevice,
  sendWrite: SendWrite,
  flashSettleMs: number,
  columnCount: number,
  staticCustomLightRgb: Record<number, Record<number, Record<number, CommonAddress>>>,
  staticLightR0C0: CommonAddress[],
  keyMap: keyMapLighting,
  index: number,
): Promise<void[]> {
  const rgbMap = staticCustomLightRgb[index]
  if (!rgbMap) return []

  const addrLock = staticLightR0C0[index]
  if (addrLock) {
    await sendWrite(device, addrLock, [])
  }

  const setPromises: Promise<void>[] = []
  for (const key of Object.values(keyMap)) {
    const { index: keyIdx, rgb } = key
    const { row, col } = indexToRowCol(keyIdx, columnCount)
    const addr = rgbMap[row]?.[col]
    if (!addr) continue
    const sendData = new Uint8Array(4)
    sendData[0] = rgb?.r ?? 0xff
    sendData[1] = rgb?.g ?? 0xff
    sendData[2] = rgb?.b ?? 0xff
    sendData[3] = 0x00
    setPromises.push(sendWrite(device, addr, Array.from(sendData)))
  }

  const result = await Promise.all(setPromises)
  if (addrLock) {
    const unlockAddr = [...addrLock]
    unlockAddr[0] = unlockAddr[0] + 0x01
    await sendWrite(device, unlockAddr, [])
  }

  device.cache.invalidateByTag(`light:static:${index}`)
  await delay(flashSettleMs)
  return result
}

/** 读取静态灯效逐键 RGB 颜色 */
export async function getKeysColor(
  device: BaseDevice,
  sendRead: SendRead,
  domain: LightingDomainType,
  index: number,
  columnCount: number,
  rowCount: number,
  staticCustomLightRgb: Record<number, Record<number, Record<number, CommonAddress>>>,
): Promise<keyMapLighting> {
  const result: keyMapLighting = []
  for await (const keyRgb of getKeysColorStream(
    device,
    sendRead,
    domain,
    index,
    columnCount,
    rowCount,
    staticCustomLightRgb,
  )) {
    result.push(keyRgb)
  }
  return result
}

/** 流式读取静态灯效逐键 RGB 颜色（批量读取减少通信次数） */
export async function* getKeysColorStream(
  device: BaseDevice,
  sendRead: SendRead,
  domain: LightingDomainType,
  index: number,
  columnCount: number,
  rowCount: number,
  staticCustomLightRgb: Record<number, Record<number, Record<number, CommonAddress>>>,
): AsyncIterable<keyRgbItem> {
  const LayoutKeys = device.manifest.layout.keys
  const effects = device.manifest.capabilities.find(
    (item): item is LightingCapability => item.type === 'lighting',
  )?.config.effects

  if (effects && effects.some((e) => e.id === index)) {
    const rgbColor = await getProperty(device, sendRead, domain, index, 'rgb')
    if (rgbColor) {
      yield* LayoutKeys.flat().map((k) => ({
        index: k.key,
        rgb: rgbColor as RGB,
      }))
      return
    }
  }

  const rgbMap = staticCustomLightRgb[index]
  if (!rgbMap) return

  const keyList: { row: number; col: number; address: CommonAddress }[] = []
  for (const rowStr in rgbMap) {
    const row = parseInt(rowStr)
    const colMap = rgbMap[row]
    for (const colStr in colMap) {
      const col = parseInt(colStr)
      keyList.push({ row, col, address: colMap[col] })
    }
  }

  const BYTE_SIZE = 4
  const BATCH_SIZE = 6
  for (let i = 0; i < keyList.length; i += BATCH_SIZE) {
    const batchKeys = keyList.slice(i, i + BATCH_SIZE)
    const batchLength = batchKeys.length
    const readLength = batchLength * BYTE_SIZE
    const firstAddress = batchKeys[0].address

    const hasValidKey = batchKeys.some((k) =>
      isValidRowCol(k.row, k.col, columnCount, rowCount, LayoutKeys),
    )
    if (!hasValidKey) continue

    const batchData = await sendRead(
      device,
      firstAddress,
      readLength,
      (data: DataView) => {
        const result = new Uint8Array(readLength)
        for (let j = 0; j < readLength; j++) {
          result[j] = data.getUint8(4 + j)
        }
        return result
      },
      [`light:static:${index}`],
    )

    for (let idx = 0; idx < batchLength; idx++) {
      const key = batchKeys[idx]
      if (!isValidRowCol(key.row, key.col, columnCount, rowCount, LayoutKeys)) continue
      const rgbStart = idx * BYTE_SIZE
      const rgb = {
        r: batchData[rgbStart],
        g: batchData[rgbStart + 1],
        b: batchData[rgbStart + 2],
      }
      yield { index: rowColToIndex(key.row, key.col, columnCount), rgb }
    }
  }
}

// ==================== 自定义灯效 ====================

/** 创建自定义静态灯效（含名称/颜色/渐变配置） */
export async function createCustomEffect(
  device: BaseDevice,
  sendWrite: SendWrite,
  customStaticNameAddrLen: number,
  customStaticNameMaxLen: number,
  staticLightEffectAddressNameLevel: CommonAddress[],
  staticLightType: CommonAddress[],
  config: keyboardLightingCustomConf,
  index: number,
): Promise<void> {
  const nameAddressList = staticLightEffectAddressNameLevel
  if (!nameAddressList || index >= nameAddressList.length) {
    throw new DeviceError(
      DeviceErrorCode.MAX_LIMIT_REACHED,
      `Max custom static effect count is ${nameAddressList?.length ?? 0}`,
    )
  }

  const type = config.staticType ?? 1
  const brightness = config.brightness ?? 50
  const hasName = config.name !== undefined
  const rgbGroup = config.rgbGroup || []
  const startColor = rgbGroup[0] ?? { r: 255, g: 255, b: 255 }
  const endColor = rgbGroup[1] ?? { r: 0, g: 0, b: 0 }

  if (hasName) {
    const addr = getAddress(nameAddressList, index)
    const sendData = new Uint8Array(customStaticNameAddrLen)
    const nameData = new Uint8Array(customStaticNameMaxLen)
    const nameASCII = convertStrToASCII(config.name || '')
    nameData.set(nameASCII.slice(0, customStaticNameMaxLen), 0)
    sendData.set(nameData, 0)
    sendData[12] = type
    sendData[13] = brightness
    if (type === 1) {
      sendData[14] = 0xff // 静态灯效0~4:默认显示 0xff:显示为对应的RGB存储区
      sendData[15] = 0xff // 驱动自定义(固件默认是0xf)
      sendData[16] = 1 // 颜色类型说明:0=-随机 1==指定颜色RGB
      sendData[17] = 0xff // r
      sendData[18] = 0xff // g
      sendData[19] = 0xff // b
    } else if ([2, 3].includes(type)) {
      sendData.set(
        [startColor.r, startColor.g, startColor.b, endColor.r, endColor.g, endColor.b],
        14,
      )
    }
    return sendWrite(device, addr, Array.from(sendData)).then(() => {
      device.cache.invalidateByTag(`light:static:${index}`)
    })
  } else {
    const domain = LightingDomainRegistry.get(LightingDomainType.Static)
    const buffer = domain.parser.serialize({
      index,
      domainType: LightingDomainType.Static,
      gradientDirection: type,
      brightness,
      rgbGroup: [2, 3].includes(type) ? [startColor, endColor] : undefined,
    } as LightingConfigData)
    const addr = getAddress(staticLightType, index)
    return sendWrite(device, addr, Array.from(buffer)).then(() => {
      device.cache.invalidateByTag(`light:static:${index}`)
    })
  }
}

/** 删除自定义灯效，若当前正在使用则先切换到默认灯效 */
export async function deleteCustomEffect(
  device: BaseDevice,
  sendRead: SendRead,
  sendWrite: SendWrite,
  checkACK: CheckACK,
  flashSettleMs: number,
  backlightEffect: LevelAddress,
  deleteLightingCmd: number[],
  deleteReportId: number,
  deleteFeatureReportId: number,
  index: number,
): Promise<number> {
  const DEFAULT_EFFECT = 100
  let effectIdx = await getCurrentEffect(device, sendRead, backlightEffect)
  if (effectIdx === index) {
    effectIdx = DEFAULT_EFFECT
    await setCurrentEffect(device, sendWrite, flashSettleMs, backlightEffect, effectIdx)
  }

  const connectType = device.connectionType
  const payload = new Uint8Array([connectType, deleteLightingCmd[0], index])
  const res = await device.sendAndWait(
    deleteReportId,
    payload,
    deleteFeatureReportId,
    (res: DataView) => res.getUint8(0) !== 0x02,
  )
  checkACK(res)
  device.cache.invalidateByTag(`light:static:${index}`)
  return effectIdx
}

// ==================== 侧灯操作 ====================

/**
 * 侧灯/充电灯/氛围灯 4 字节使能寄存器通用布局：
 *   byte0: 充电灯开关使能（0=常开, 1=跟随氛围灯开关）
 *   byte1: 氛围灯开关（0=关, 1=开）
 *   byte2: 侧灯模式（0=单色常亮, 1=RGB轮询, 2=单        色呼吸）
 *   byte3: 侧灯开关（0=关, 1=开）
 *
 * CH585M 与 Telink 布局一致，仅基址不同（CH585M=+0x00B0, Telink=+0x0084）。
 */

/** 读取 4 字节使能寄存器原始数据 */
export async function getSideLightEnableBytes(
  device: BaseDevice,
  sendRead: SendRead,
  enableAddr: LevelAddress,
): Promise<Uint8Array> {
  const addr = getAddress(enableAddr, device.currentConfig.index)
  return sendRead(device, addr, 8, (data: DataView) => {
    const buf = new Uint8Array(4)
    for (let i = 0; i < 4; i++) buf[i] = data.getUint8(4 + i)
    return buf
  })
}

/** 写入 4 字节使能寄存器 */
export async function setSideLightEnableBytes(
  device: BaseDevice,
  sendWrite: SendWrite,
  flashSettleMs: number,
  enableAddr: LevelAddress,
  bytes: number[],
): Promise<void> {
  const addr = getAddress(enableAddr, device.currentConfig.index)
  await sendWrite(device, addr, bytes)
  await delay(flashSettleMs)
}

/** 将 4 字节原始数据解析为 AuxLightingEnableRegister */
export function parseAuxLightingEnableBytes(bytes: Uint8Array): AuxLightingEnableRegister {
  return {
    chargeLightFollowAmbient: bytes[0] === 1,
    ambientLightEnabled: bytes[1] === 1,
    sideLightMode: bytes[2],
    sideLightEnabled: bytes[3] === 1,
  }
}

/** 将 AuxLightingEnableRegister 序列化为 4 字节原始数据 */
export function serializeAuxLightingEnableRegister(reg: AuxLightingEnableRegister): Uint8Array {
  const buf = new Uint8Array(4)
  buf[0] = reg.chargeLightFollowAmbient ? 1 : 0
  buf[1] = reg.ambientLightEnabled ? 1 : 0
  buf[2] = reg.sideLightMode
  buf[3] = reg.sideLightEnabled ? 1 : 0
  return buf
}
